import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  ExerciseConstructorContent,
  RecordAudioExerciseContent,
} from "content";
import {
  useAddExercise,
  useAddHomeworkExercise,
  useEditExercise,
  useEditHomeworkExercise,
} from "hooks/api/exercises";
import { useExerciseConstructor } from "hooks/redux/useExerciseConstructor";
import { Textarea } from "components/Interactive/Textarea";
import { Select } from "components/Interactive/Select";
import { Space } from "components/Common/Space";
import { ExerciseConfigForm } from "../../ExerciseConfigForm";
import { logError, logSuccess } from "utils/notifications";
import { TAddExercise, TEditExercise } from "types/api/exercise";
import {
  EExerciseType,
  TRecordAudioExercisePayload,
} from "types/app/exercises";
import { TSelectOption } from "types/app/select";
import { TRecordAudioExerciseForm } from "./RecordAudioExerciseForm.types";

const DURATION_OPTIONS: TSelectOption<number>[] = [
  { value: 10, label: "10 сек" },
  { value: 30, label: "30 сек" },
  { value: 60, label: "1 хв" },
  { value: 120, label: "2 хв" },
  { value: 300, label: "5 хв" },
];

export const RecordAudioExerciseForm: React.FC = () => {
  const {
    courseId,
    lessonId,
    chapterId,
    homeworkId,
    editExercise,
    isEditMode,
    constructorMode,
    closeAll,
  } = useExerciseConstructor<TRecordAudioExercisePayload>();

  const { mutate: addExercise } = useAddExercise<TRecordAudioExercisePayload>(
    courseId,
    lessonId,
    chapterId
  );
  const { mutate: addHomeworkExercise } =
    useAddHomeworkExercise<TRecordAudioExercisePayload>(
      courseId,
      lessonId,
      homeworkId
    );
  const { mutate: editExerciseHandler } =
    useEditExercise<TRecordAudioExercisePayload>(courseId, lessonId, chapterId);
  const { mutate: editHomeworkExerciseHandler } =
    useEditHomeworkExercise<TRecordAudioExercisePayload>(
      courseId,
      lessonId,
      homeworkId
    );

  const defaultValues: Partial<TRecordAudioExerciseForm> = useMemo(
    () => ({
      text: editExercise?.payload.text || "",
      description: editExercise?.payload.data.description || "",
      maxDurationSeconds: DURATION_OPTIONS.find(
        (o) => o.value === editExercise?.payload.data.maxDurationSeconds
      ),
    }),
    [editExercise]
  );

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<TRecordAudioExerciseForm>({
    mode: "onBlur",
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const successCallback = () => {
    logSuccess(
      isEditMode
        ? ExerciseConstructorContent.Notification.Edit.SUCCESS
        : ExerciseConstructorContent.Notification.Add.SUCCESS
    );
    closeAll();
    reset(defaultValues);
  };

  const buildPayload = (
    formData: TRecordAudioExerciseForm
  ): TAddExercise<TRecordAudioExercisePayload> => ({
    type: EExerciseType.RECORD_AUDIO,
    payload: {
      text: formData.text,
      data: {
        description: formData.description,
        maxDurationSeconds: formData.maxDurationSeconds.value,
      },
    },
  });

  const exerciseSubmitHandler = (formData: TRecordAudioExerciseForm) => {
    if (!courseId || !lessonId || !(chapterId || homeworkId)) {
      logError({ message: ExerciseConstructorContent.Notification.Add.ERROR });
      return;
    }

    const addBody = buildPayload(formData);

    if (!isEditMode && constructorMode === "lesson") {
      return addExercise(addBody, { onSuccess: successCallback });
    }
    if (!isEditMode && constructorMode === "homework") {
      return addHomeworkExercise(addBody, { onSuccess: successCallback });
    }
    if (isEditMode && editExercise) {
      const editBody: TEditExercise<TRecordAudioExercisePayload> = {
        ...addBody,
        id: editExercise.id,
      };
      if (constructorMode === "lesson") {
        return editExerciseHandler(editBody, { onSuccess: successCallback });
      }
      if (constructorMode === "homework") {
        return editHomeworkExerciseHandler(editBody, {
          onSuccess: successCallback,
        });
      }
    }
  };

  return (
    <ExerciseConfigForm
      onSubmit={handleSubmit(exerciseSubmitHandler)}
      isEditMode={isEditMode}
    >
      <Space direction="vertical" justify="start" size="medium" fullWidth>
        <Textarea<TRecordAudioExerciseForm>
          name="text"
          id="record-audio-exercise-text"
          label={RecordAudioExerciseContent.Add.Input.Text.LABEL}
          placeholder={RecordAudioExerciseContent.Add.Input.Text.PLACEHOLDER}
          register={register}
          errors={errors}
          required
          fullWidth
        />
        <Textarea<TRecordAudioExerciseForm>
          name="description"
          id="record-audio-exercise-description"
          label={RecordAudioExerciseContent.Add.Input.Description.LABEL}
          placeholder={
            RecordAudioExerciseContent.Add.Input.Description.PLACEHOLDER
          }
          register={register}
          errors={errors}
          required
          fullWidth
        />
        <Select<TRecordAudioExerciseForm>
          name="maxDurationSeconds"
          id="record-audio-exercise-max-duration"
          label={RecordAudioExerciseContent.Add.Input.MaxDuration.LABEL}
          placeholder={
            RecordAudioExerciseContent.Add.Input.MaxDuration.PLACEHOLDER
          }
          options={DURATION_OPTIONS}
          control={control}
          errors={errors}
          required
          fullWidth
        />
      </Space>
    </ExerciseConfigForm>
  );
};
